import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Calendar } from 'lucide-react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface Employee {
  id: string;
  name: string;
}

export interface VacationEntryEdit {
  id: string;
  employee_profile_id: string;
  start_date: string;
  end_date: string;
}

interface AddVacationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employeeId: string, startDate: string, endDate: string, daysTaken: number) => Promise<void>;
  onUpdate?: (id: string, employeeId: string, startDate: string, endDate: string, daysTaken: number) => Promise<void>;
  employees: Employee[];
  initialEntry?: VacationEntryEdit | null;
}

const AddVacationModal: React.FC<AddVacationModalProps> = ({ isOpen, onClose, onSave, onUpdate, employees, initialEntry }) => {
  const isEdit = Boolean(initialEntry);
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const daysCount = startDate && endDate ? (() => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (end < start) return 0;
    return differenceInCalendarDays(end, start) + 1;
  })() : 0;

  useEffect(() => {
    if (isOpen) {
      if (initialEntry) {
        setEmployeeId(initialEntry.employee_profile_id);
        setStartDate(initialEntry.start_date);
        setEndDate(initialEntry.end_date);
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setStartDate(today);
        setEndDate(today);
        setEmployeeId(employees[0]?.id || '');
      }
    }
  }, [isOpen, employees, initialEntry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeId || !startDate || !endDate || daysCount < 1) return;
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (end < start) return;
    setLoading(true);
    try {
      const days = differenceInCalendarDays(end, start) + 1;
      if (isEdit && initialEntry && onUpdate) {
        await onUpdate(initialEntry.id, employeeId, startDate, endDate, days);
      } else {
        await onSave(employeeId, startDate, endDate, days);
      }
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden animate-fade-in"
          onInteractOutside={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest('[data-radix-select-viewport], [role="listbox"], [role="option"]')) e.preventDefault();
          }}
        >
          <div className="px-6 pt-6 pb-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title asChild>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-500" />
                  {isEdit ? 'Editar vacaciones' : 'Registrar vacaciones'}
                </h2>
              </Dialog.Title>
              <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Select
                label="Empleado"
                value={employeeId}
                onChange={setEmployeeId}
                options={employees.map((e) => ({ value: e.id, label: e.name }))}
              />
              <Input
                label="Fecha inicio"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                label="Fecha fin"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
              {daysCount > 0 && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                  {daysCount} {daysCount === 1 ? 'día corrido' : 'días corridos'} — se descontarán del disponible
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={loading || daysCount < 1} className="flex-1">{loading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Registrar'}</Button>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddVacationModal;
